import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Form as BootstrapForm } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";
import { debounce } from "lodash";
import posthog from "posthog-js";
import { axiosBasic, getUserInfo } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { Common } from "../../../../../utils";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import { useHistory } from "react-router-dom";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";

import "../Settings.css";
import "./CarryForward.css";

const CarryForward = () => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});

  // Fetch leave types from API
  const fetchLeaveTypes = async () => {
    if (isSuspended) return;
    const userId = getUserInfo()?.selfUserId ?? null;
    const userInput = {};
    try {
      setLoading(true);
      const response = await axiosBasic.get(config.REST_API.LeaveType.GetLeaveTypesOfCompany);

      const success = response.data && response.data.success;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchLeaveTypes", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : (response.data?.message || "Failed to fetch leave types"),
        });
      }

      if (success) {
        const leaveTypesData = response.data.data;

        // Filter only active leave types (removed leavesCount > 0 requirement)
        const filteredLeaveTypes = leaveTypesData.filter(leaveType =>
          leaveType.active
        );

        setLeaveTypes(filteredLeaveTypes);

        // Initialize form data with leave types
        const initialFormData = {};
        filteredLeaveTypes.forEach(leaveType => {
          initialFormData[leaveType.id] = {
            enabled: leaveType.allowCarryForward || false,
            totalDays: leaveType.maxCarryForward || 0
          };
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("fetchLeaveTypes", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  useEffect(() => {
    if (!isSuspended) {
      fetchLeaveTypes();
    }
  }, [isSuspended]);

  // Debounced function to update carry forward settings on server
  const updateCarryForwardOnServer = useCallback(
    debounce(async (leaveTypeId, updatedData, previousData) => {
      const userId = getUserInfo()?.selfUserId ?? null;
      const userInput = { leaveTypeId, enabled: updatedData?.enabled, totalDays: updatedData?.totalDays };
      try {
        const response = await axiosBasic.put(config.REST_API.CarryForward.UpdateCarryForward, {
          Id: leaveTypeId,
          AllowCarryForward: updatedData.enabled,
          MaxCarryForward: updatedData.totalDays
        });

        const success = response.data && response.data.success;
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("updateCarryForward", {
            type: success ? "info" : "exception",
            userId,
            success,
            userInput,
            reason: success ? undefined : (response.data?.message || "Failed to update carry forward settings"),
          });
        }

        if (success) {
          Common.showSuccessMessage(t("app.carryForward.successUpdate"));
          // Update the local leave types state
          setLeaveTypes(prev =>
            prev.map(leaveType =>
              leaveType.id === leaveTypeId
                ? { ...leaveType, allowCarryForward: updatedData.enabled, maxCarryForward: updatedData.totalDays }
                : leaveType
            )
          );
        } else {
          // Rollback to previous value on API error
          setFormData(prev => ({
            ...prev,
            [leaveTypeId]: previousData
          }));
          Common.showErrorMessage(response.data?.message || t("app.carryForward.errorUpdate"));
        }
      } catch (error) {
        if (typeof posthog !== "undefined" && posthog.capture) {
          posthog.capture("updateCarryForward", {
            type: "exception",
            userId,
            success: false,
            userInput,
            reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          });
        }
        // Rollback to previous value on network error
        setFormData(prev => ({
          ...prev,
          [leaveTypeId]: previousData
        }));
        Common.showErrorMessage(error?.response?.data?.message || t("app.carryForward.errorUpdate"));
      }
    }, 1000), // 1 second debounce
    [isSuspended]
  );

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  // Dynamic validation schema based on leave types
  const getValidationSchema = () => {
    const schemaFields = {};
    leaveTypes.forEach(leaveType => {
      schemaFields[leaveType.id] = Yup.object({
        enabled: Yup.boolean().required(t("app.carryForward.validationRequired")),
        totalDays: Yup.number()
          .integer(t("app.carryForward.validationInteger"))
          .min(0, t("app.carryForward.validationMin"))
          .max(365, t("app.carryForward.validationMax"))
          .required(t("app.carryForward.validationRequired")),
      });
    });
    return Yup.object(schemaFields);
  };

  const validationSchema = getValidationSchema();

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
  };

  // Helper function to get icon and color for leave type
  const getLeaveTypeIcon = (leaveTypeName) => {
    const name = leaveTypeName.toLowerCase();
    if (name.includes('sick') || name.includes('medical')) {
      return { icon: "heart", color: "#fa709a" };
    } else if (name.includes('annual') || name.includes('vacation')) {
      return { icon: "calendar alternate outline", color: "#4facfe" };
    } else if (name.includes('maternity') || name.includes('paternity')) {
      return { icon: "baby", color: "#f093fb" };
    } else if (name.includes('casual') || name.includes('personal')) {
      return { icon: "clock outline", color: "#43e97b" };
    } else {
      return { icon: "calendar", color: "#667eea" };
    }
  };

  const handleLeaveChange = (type, field, value) => {
    // Store previous data for rollback
    const previousData = { ...formData[type] };

    const updatedFormData = {
      ...formData,
      [type]: { ...formData[type], [field]: value },
    };

    setFormData(updatedFormData);

    // Find the leave type and update on server
    const leaveType = leaveTypes.find(lt => lt.id === type);
    if (leaveType) {
      updateCarryForwardOnServer(type, updatedFormData[type], previousData);
    }
  };

  const LeaveRow = ({ leaveType }) => {
    const leave = formData[leaveType.id] || {};
    const { icon, color } = getLeaveTypeIcon(leaveType.name);
    const isDisabled = !leaveType.active;

    return (
      <div className={`carry-forward-card ${isDisabled ? 'disabled' : ''}`}>
        {/* Card Header */}
        <div className="card-header">
          <div className="leave-type-info">
            <div className="leave-icon" style={{ background: color }}>
              <Icon name={icon} />
            </div>
            <div className="leave-details">
              <h3 className="leave-name">{leaveType.name}</h3>
              <p className="leave-description">
                {isDisabled ? t("app.carryForward.leaveTypeInactive") : t("app.carryForward.setMaxDaysHelper")}
              </p>
            </div>
          </div>
          <div className="card-status">
            {isDisabled ? (
              <span className="status-badge inactive">
                <Icon name="ban" />
                {t("app.departments.inactive")}
              </span>
            ) : leave.enabled ? (
              <span className="status-badge active">
                <Icon name="check circle" />
                {t("settings.general.enabled")}
              </span>
            ) : (
              <span className="status-badge disabled">
                <Icon name="times circle" />
                {t("settings.general.disabled")}
              </span>
            )}
          </div>
        </div>

        {/* Card Content */}
        <div className="card-content">
          <div className="settings-section">
            <div className="setting-item">
              <div className="setting-label">
                <Icon name="refresh" />
                <span>{t("app.carryForward.allowCarryForward")}</span>
              </div>
              <div className="setting-control">
                <BootstrapForm.Check
                  type="switch"
                  id={`switch-${leaveType.id}`}
                  className="carry-forward-switch"
                  checked={leave.enabled}
                  disabled={isDisabled}
                  onChange={(e) =>
                    handleLeaveChange(leaveType.id, "enabled", e.target.checked)
                  }
                />
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-label">
                <Icon name="calendar" />
                <span>{t("app.carryForward.maximumDays")}</span>
                <small>{t("app.carryForward.maximumDaysHelper")}</small>
              </div>
              <div className="setting-control">
                <div className="input-group">
                  <BootstrapForm.Control
                    type="number"
                    value={leave.totalDays}
                    disabled={!leave.enabled || isDisabled}
                    className="days-input"
                    min="0"
                    max={leaveType.defaultLeaveCount || 365}
                    placeholder="0"
                    onChange={(e) =>
                      handleLeaveChange(leaveType.id, "totalDays", Number(e.target.value))
                    }
                  />
                  <span className="input-suffix">{t("app.carryForward.days")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          {leave.enabled && (
            <div className="summary-section">
              <div className="summary-item">
                <Icon name="info circle" />
                <span>{t("app.carryForward.carryForwardEnabledFor", { count: leave.totalDays })}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="carry-forward-container">
      {/* Header Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="refresh" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("app.carryForward.title")}</h3>
            <p className="section-description">
              {t("app.carryForward.description")}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="settings-page-content">
        <div className="content-card">
          <div className="info-section">
            <div className="info-icon">
              <Icon name="info circle" />
            </div>
            <div className="info-text">
              <h3>{t("app.carryForward.howCarryForwardWorks")}</h3>
              <p>
                {t("app.carryForward.howCarryForwardDescription")}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <Icon name="spinner" loading size="large" />
              <p>{t("app.carryForward.loadingLeaveTypes")}</p>
            </div>
          ) : leaveTypes.length === 0 ? (
            <div className="no-data-container">
              <Icon name="info circle" size="large" />
              <p>{t("app.carryForward.noActiveLeaveTypes")}</p>
            </div>
          ) : (
            <Formik
              initialValues={formData}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {(formik) => (
                <Form>
                  <div className="leave-types-grid">
                    {leaveTypes.map((leaveType) => (
                      <LeaveRow
                        key={leaveType.id}
                        leaveType={leaveType}
                      />
                    ))}
                  </div>

                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarryForward;
